import { isSafariBrowser } from '@modules/common/utils';
import AudioBookmarkController from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/AudioBookmarkController';
import AudioRateMenu from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/AudioRateMenu';
import AudioSeekBar from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/AudioSeekBar';
import AudioSummaryPreview from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/AudioSummaryPreview';
import AudioTranscriptToggleButton from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/AudioTranscriptToggleButton';
import {
  useAudioFeedbackSnippetShareControls,
  useAudioFeedbackTranscriptControls,
  useGetIsSavedSummary,
} from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/hooks';
import { useAudioFeedbackPlayerControls } from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/hooks/useAudioFeedbackPlayerControls';
import PlayPauseButtonGroup from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/PlayPauseButtonGroup';
import {
  getAudioHighlightMetadata,
  getNoAudioSourceInfoText,
  isEmptyAudioSourceFeedback,
  isSummaryHighlightMetadataType,
} from '@modules/feedback/components/AudioType/utils';
import { FeedbackListViewType } from '@modules/feedback/constants';
import useFeedbackListStore from '@modules/feedback/store/useFeedbackListStore';
import { Box, Stack } from '@mui/material';
import { memo, useEffect, useMemo, useRef } from 'react';
import ReactAudioPlayer from 'react-audio-player';
import { v4 as uuidv4 } from 'uuid';
import {
  Feedback,
  FeedbackContentType,
  FeedbackLabel,
} from 'v2/core/entities/feedback';
import FeedbackSummaryInfoMessage from './FeedbackSummaryInfoMessage';

export const JUMP_BUFFER_TIME = 10; // seconds
const LISTEN_INTERVAL = 1; // second

interface AudioFeedbackPlayerProps {
  feedback: Feedback;
  selectedLabels: FeedbackLabel[];
  setSelectedLabels: (selectedLabels: FeedbackLabel[]) => void;
  isFeedbackCardView?: boolean;
  shouldAutoPause?: boolean;
  onExpandFeedbackDetails?: () => void;
  shouldShowSavedFeedbackCard?: boolean;
  feedbackListViewType?: FeedbackListViewType;
}

const AudioFeedbackPlayer = ({
  selectedLabels,
  setSelectedLabels,
  isFeedbackCardView = false,
  shouldAutoPause = false,
  onExpandFeedbackDetails,
  feedback,
  feedbackListViewType = FeedbackListViewType.DEFAULT,
  shouldShowSavedFeedbackCard = false,
}: AudioFeedbackPlayerProps) => {
  const isTranscriptPopoverOpened = useFeedbackListStore(
    (state) => state.feedbacks[feedback.id]?.isTranscriptPopoverOpened ?? true,
  );

  const isSavedSummary = useGetIsSavedSummary();

  const isMounted = useRef(false);

  const audioElementID = useMemo(
    () => `audio-player-${feedback.id}-${uuidv4()}`,
    [feedback.id],
  );

  const audioConversation = useMemo(
    () =>
      feedback.content.type === FeedbackContentType.Audio
        ? feedback.content.audioConversationEn
        : [],
    [feedback.content],
  );

  const highlightMetadata = useMemo(() => {
    const audioHighlightMetadata = getAudioHighlightMetadata(
      isFeedbackCardView ? selectedLabels : feedback.labels ?? [],
      feedback.labels ?? [],
      audioConversation,
    );

    if (
      !(
        isFeedbackCardView &&
        feedbackListViewType === FeedbackListViewType.SAVED
      )
    ) {
      return audioHighlightMetadata;
    }

    return audioHighlightMetadata.filter((metadata) => {
      return (
        isSummaryHighlightMetadataType(metadata) &&
        isSavedSummary({
          feedbackID: feedback.id,
          snippetID: metadata.summaryText,
        })
      );
    });
  }, [
    selectedLabels,
    feedback.labels,
    feedback.id,
    audioConversation,
    isFeedbackCardView,
    feedbackListViewType,
    isSavedSummary,
  ]);

  const isEmptyAudioSource = isEmptyAudioSourceFeedback(feedback);

  const {
    audioRef,
    states: {
      canPlay,
      rate,
      isPlaying,
      isSeeking,
      duration,
      seek,
      shouldLoadAudio,
    },
    handlers: {
      handleOnLoadedMetadata,
      handleOnJumpSeek,
      handleOnJumpSeekWithBuffer,
      handleOnTogglePlay,
      handleOnCanPlay,
      handleOnPlay,
      handleOnPause,
      handleOnListen,
      handleOnRewind,
      handleOnForward,
      handleOnChangeRate,
      handleOnJumpToBookmark,
      handleOnChangeSeekBar,
      handleOnCommitSeekBar,
      handleOnError,
      handleOnResetStates,
    },
  } = useAudioFeedbackPlayerControls({
    feedbackID: feedback.id,
    audioElementID,
    jumpBufferTime: JUMP_BUFFER_TIME,
    audioConversation,
    shouldAutoPause,
    isEmptyAudioSource,
  });

  useAudioFeedbackTranscriptControls({
    feedbackID: feedback.id,
    isFeedbackCardView,
    isSeeking,
    isPlaying,
    highlightMetadata,
    selectedLabels,
    conversation: audioConversation,
    onJumpSeekWithBuffer: handleOnJumpSeekWithBuffer,
    onJumpSeek: handleOnJumpSeek,
    onTogglePlay: handleOnTogglePlay,
    isEmptyAudioSource,
  });

  useAudioFeedbackSnippetShareControls({
    feedbackID: feedback.id,
    feedbackLabels: feedback.labels,
    conversation: audioConversation,
    isPlaying,
    isFeedbackCardView,
    onJumpSeek: handleOnJumpSeek,
    onTogglePlay: handleOnTogglePlay,
    setSelectedLabels,
    isEmptyAudioSource,
  });

  useEffect(() => {
    if (shouldAutoPause) {
      handleOnResetStates({
        skipTranscriptPopoverState: true,
      });
      return;
    }
    if (isFeedbackCardView) {
      return;
    }
    // reset audio states & persist rate on moving to other feedback
    if (isMounted.current === false) {
      isMounted.current = true;
      return;
    }
    handleOnResetStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback.id, shouldAutoPause, isFeedbackCardView]);

  const getAudioPreloadConfig = () => {
    if (!shouldLoadAudio || isEmptyAudioSource) {
      return 'none';
    }
    return isSafariBrowser() ? 'auto' : 'metadata';
  };

  const preloadConfig = getAudioPreloadConfig();

  const isTranscriptPopoverVisible =
    !isFeedbackCardView && isTranscriptPopoverOpened;

  const audioSrc =
    feedback.content.type === FeedbackContentType.Audio
      ? feedback.content.audioFile
      : '';

  const disabledControlsTooltipTitle = `Playback controls are disabled as ${getNoAudioSourceInfoText(
    feedback.sourceV2,
    true,
  )}`;

  const shouldShowFeedbackSummaryInfoMessage =
    selectedLabels.length === 0 &&
    feedbackListViewType !== FeedbackListViewType.SAMPLE;

  return (
    <Box
      id={isFeedbackCardView ? undefined : 'js-feedback-details-audio-player'}
    >
      <Box
        sx={{
          borderRadius: 2,
          borderTopLeftRadius: isTranscriptPopoverVisible ? 0 : undefined,
          borderTopRightRadius: isTranscriptPopoverVisible ? 0 : undefined,
          backgroundColor: 'feedback.audioPlayer',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: !isFeedbackCardView
            ? 'divider'
            : shouldShowSavedFeedbackCard
            ? (theme) => theme.palette.background.paper
            : 'transparent',
          outline: shouldShowSavedFeedbackCard ? '2px solid' : undefined,
          outlineColor: shouldShowSavedFeedbackCard
            ? (theme) => theme.palette.background.paper
            : undefined,
        }}
      >
        <ReactAudioPlayer
          id={audioElementID}
          ref={(ref) => {
            audioRef.current = ref;
          }}
          src={!isEmptyAudioSource ? audioSrc : undefined}
          preload={preloadConfig}
          listenInterval={LISTEN_INTERVAL * 1000}
          onListen={handleOnListen}
          onLoadedMetadata={handleOnLoadedMetadata}
          onError={handleOnError}
          onCanPlay={handleOnCanPlay}
          onPlay={handleOnPlay}
          onPause={handleOnPause}
          onAbort={handleOnPause}
          onEnded={handleOnPause}
        />
        <Stack
          sx={{ px: 2, py: 1 }}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <PlayPauseButtonGroup
            isPlaying={isPlaying}
            onTogglePlay={handleOnTogglePlay}
            onRewind={handleOnRewind}
            onForward={handleOnForward}
            isRewindDisabled={seek === 0}
            isForwardDisabled={seek === duration}
            feedbackID={feedback.id}
            isLoading={!canPlay && isPlaying}
            isDisabled={isEmptyAudioSource}
            disabledTooltipTitle={disabledControlsTooltipTitle}
          />
          <Stack direction="row" alignItems="center" width="100%">
            <AudioSeekBar
              value={seek}
              duration={duration}
              onChange={handleOnChangeSeekBar}
              onCommit={handleOnCommitSeekBar}
              onJumpToBookmark={handleOnJumpToBookmark}
              highlightMetadata={highlightMetadata}
              feedback={feedback}
              selectedLabels={selectedLabels}
              isFeedbackCardView={isFeedbackCardView}
              feedbackListViewType={feedbackListViewType}
              isSliderDisabled={isEmptyAudioSource}
              isBookmarkDisabled={isEmptyAudioSource && isFeedbackCardView}
            />
            <Stack direction="row" alignItems="center">
              {!isFeedbackCardView && (
                <AudioTranscriptToggleButton feedbackID={feedback.id} />
              )}
              {!isEmptyAudioSource && (
                <Box ml={2}>
                  <AudioRateMenu
                    value={rate}
                    onChange={handleOnChangeRate}
                    feedbackID={feedback.id}
                  />
                </Box>
              )}
            </Stack>
          </Stack>
        </Stack>
        {!isEmptyAudioSource && (
          <AudioBookmarkController
            highlightMetadata={highlightMetadata}
            seek={seek}
            onJumpToBookmark={handleOnJumpToBookmark}
            selectedLabels={selectedLabels}
            isAudioPlaying={isPlaying}
            feedbackID={feedback.id}
            isFeedbackCardView={isFeedbackCardView}
          />
        )}
        {isFeedbackCardView && (
          <AudioSummaryPreview
            feedback={feedback}
            highlightMetadata={highlightMetadata}
            selectedLabels={selectedLabels}
            allLabels={feedback.labels ?? []}
            seek={seek}
            audioConversation={audioConversation}
            onExpandFeedbackDetails={onExpandFeedbackDetails}
            feedbackListViewType={feedbackListViewType}
          />
        )}
        {shouldShowFeedbackSummaryInfoMessage && (
          <FeedbackSummaryInfoMessage
            hasSummary={feedback.labels?.length !== 0}
          />
        )}
      </Box>
    </Box>
  );
};

export default memo(AudioFeedbackPlayer);
